import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Props } from '../@types';
import { AuthContextType, User } from '../@types/auth';
import { decodePayload } from '../utils/decodePayload';
import { LoginPaper } from '../components/Login';
import {
    getAuthToken,
    getRefreshToken,
    setCookieByName,
    removeAllAuthCookies,
    invalidateKeycloakSession,
    TOKEN_NAME,
    REFRESH_TOKEN_NAME,
    KEYCLOAK_ENDPOINT,
    KEYCLOAK_USERINFO_ENDPOINT,
} from '../utils/authCookies';

const AuthContext = createContext<AuthContextType>({
    user: undefined,
    isAuthenticated: false,
    isLoading: true,
    signIn: (authToken: string, refreshToken: string) => console.log('signIn'),
    signOut: () => console.log('signOut'),
    getValidToken: () => Promise.resolve(undefined),
});

const useProvideAuth = () => {
    const [user, setUser] = useState<User>();
    const [isLoading, setIsLoading] = useState(true);

    const signOut = () => {
        invalidateKeycloakSession();
        setUser(undefined);
        localStorage.removeItem('is-locked');
        removeAllAuthCookies();
    };

    const signIn = async () => {
        const accessToken = getAuthToken();
        const refreshToken = getRefreshToken();
        if (!accessToken || !refreshToken) {
            return;
        }

        try {
            const { exp, preferred_username, family_name, given_name, email, realm_access, workplace } = decodePayload(accessToken);
            const refreshPayload = decodePayload(refreshToken);

            const resolvedUser = {
                username: preferred_username,
                name: `${family_name} ${given_name}`,
                email,
                roles: realm_access.roles,
                accessToken,
                refreshToken,
                exp,
                refreshExp: refreshPayload.exp,
                workplace,
                hasRole: (role: string) => realm_access.roles.some(r => r === role),
                isRefreshTimeExpired: () => new Date().getTime() > refreshPayload.exp * 1000,
            };
            setUser(resolvedUser);
        } catch (e) {
            console.error(e);
            signOut();
        }
    };

    const isRefreshing = useRef(false);

    const refreshAccessToken = async (): Promise<string | undefined> => {
        const refreshToken = getRefreshToken();
        if (isRefreshing.current || !KEYCLOAK_ENDPOINT || !refreshToken) {
            return;
        }

        const { exp: refreshExp } = decodePayload(refreshToken);
        if (new Date().getTime() > refreshExp * 1000) {
            signOut();
            return;
        }

        isRefreshing.current = true;

        try {
            const response = await fetch(KEYCLOAK_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    client_id: 'buszrent-crm',
                    refresh_token: refreshToken,
                    grant_type: 'refresh_token',
                }),
            });

            if (response.status === 401) {
                signOut();
                return;
            }
            const body = await response.json();
            setCookieByName(TOKEN_NAME, body.access_token);
            setCookieByName(REFRESH_TOKEN_NAME, body.refresh_token);
            await signIn();

            return body.access_token;
        } catch (e) {
            signOut();
            return;
        } finally {
            isRefreshing.current = false;
        }
    };

    const getValidToken = async (): Promise<string | undefined> => {
        const accessToken = getAuthToken();

        if (accessToken) {
            const { exp } = decodePayload(accessToken);
            const oneMinuteFromNow = new Date();
            oneMinuteFromNow.setMinutes(oneMinuteFromNow.getMinutes() + 1);

            if (oneMinuteFromNow.getTime() <= exp * 1000) {
                return accessToken;
            }
        }

        return refreshAccessToken();
    };
    const validateSession = async (): Promise<boolean> => {
        const accessToken = getAuthToken();
        if (!accessToken || !KEYCLOAK_USERINFO_ENDPOINT) {
            return false;
        }

        try {
            const response = await fetch(KEYCLOAK_USERINFO_ENDPOINT, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            return response.ok;
        } catch {
            return false;
        }
    };

    const checkTokenConsistency = () => {
        const cookieToken = getAuthToken();
        if (!cookieToken && user) {
            signOut();
            return;
        }
        if (cookieToken && user && cookieToken !== user?.accessToken) {
            signOut();
        }
    };

    useEffect(() => {
        const init = async () => {
            const isValid = await validateSession();
            if (isValid) {
                await signIn();
            } else if (getRefreshToken()) {
                await refreshAccessToken();
            } else {
                removeAllAuthCookies();
            }
            setIsLoading(false);
        };
        init();
    }, []);

    useEffect(() => {
        const onVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                checkTokenConsistency();
                await getValidToken();
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    return {
        user,
        isAuthenticated: user !== undefined && !!user.accessToken,
        isLoading,
        signIn,
        signOut,
        getValidToken,
    };
};

const useAuth = () => {
    return useContext(AuthContext);
};

const ProvideAuth = ({ children }: Props) => {
    const auth = useProvideAuth();

    if (auth.isLoading) {
        return null;
    }

    if (!auth.user) {
        return <LoginPaper />;
    }

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export { useAuth, useProvideAuth, ProvideAuth };
